// Portal shell is deliberately spartan: agency name + account context, sign
// out, a tab bar (Home / Forms), and the child page. No admin sidebar.
import { NavLink, Outlet } from "react-router-dom";
import { LogOut, LayoutDashboard } from "lucide-react";
import { usePortal } from "@/state/portal";
import { cn } from "@/lib/utils";

export function PortalShell() {
  const { me, signOut } = usePortal();
  if (!me) return null;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border bg-card/60 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold">
              <LayoutDashboard className="size-5 text-primary" />
              <span className="truncate">{me.agency.name}</span>
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {me.account.name}{me.account.city ? ` · ${me.account.city}` : ""}
            </p>
          </div>
          <button onClick={signOut} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <LogOut className="size-3.5" /> Sign out
          </button>
        </div>
        <nav className="mx-auto flex max-w-4xl gap-1 px-2">
          <PortalTab to="/portal">Home</PortalTab>
          <PortalTab to="/portal/tickets" end={false}>Requests</PortalTab>
          <PortalTab to="/portal/qr" end={false}>QR codes</PortalTab>
          <PortalTab to="/portal/help" end={false}>Help</PortalTab>
        </nav>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-border px-4 py-3 text-center text-xs text-muted-foreground">
        Signed in as {me.portal.name || me.portal.email}
      </footer>
    </div>
  );
}

function PortalTab({ to, children, end = true }: { to: string; children: React.ReactNode; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground"
        )
      }
    >
      {children}
    </NavLink>
  );
}
